import { useState, useRef } from 'react';
import { ArrowRight, Camera, Copy, Share2, Upload, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { upload_event_media } from '../Redux/Action/EventAction';
import { toast } from 'react-toastify';
import { useUrlParams } from './VideoTestimonials';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const GuestMediaUpload = ({ onClose }) => {
  const { t } = useTranslation("guestmedia");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedUpFiles, setSelectedUpFiles] = useState([]);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const { token } = useParams();
  const { id, eventId } = useUrlParams();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file =>
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );

    const filesWithPreviews = validFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video'
    }));

    setSelectedFiles(prev => [...prev, ...filesWithPreviews]);
    setSelectedUpFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== id);
    });


    setSelectedUpFiles(prev => prev.filter((_, i) => i !== id));

  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);

    dispatch(upload_event_media(eventId, selectedUpFiles)).then((message) => {
      console.log("Message: " + message)
      setUploading(false);
      alert('Media uploaded successfully!');
      if (onClose) onClose();
    }).catch((errorMsg) => {
      toast.error(errorMsg);
    });



    // Simulate upload
    // setTimeout(() => {
    //   setUploading(false);
    //   alert('Media uploaded successfully!');
    //   if (onClose) onClose(); // Use only if passed
    // }, 2000);
  };

  const mediaUrl = `${window.location.origin}/media-gallery/${token || ''}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(mediaUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // fallback
      alert('Impossible de copier le lien automatiquement. Sélectionnez et copiez manuellement: ' + mediaUrl);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Médias de l'événement",
          text: "Consultez toutes les photos et vidéos de l'événement",
          url: mediaUrl,
        });
      } catch (err) {
        // user cancelled or error
      }
    } else {
      // fallback: copy to clipboard
      await handleCopy();
      alert('Partage non disponible sur ce navigateur. Le lien a été copié.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md">
        {/* Page Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">{t("upload_photo_n_video")}</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
        <div className="p-6">
          {/* Upload Area */}
          <div className="p-4">
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center mb-4 transition-colors cursor-pointer ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">
                {t('drag_n_drop')}
              </h3>
              <p className="text-sm text-gray-600 mb-3">{t("select_files")}</p>
              <button className="bg-blue-500 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-blue-600 transition-colors">
                {t("choose_files")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>


          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="mb-6 ml-4">
              <h3 className="text-lg font-medium mb-4">
                {t("selected_files")} ({selectedFiles.length})
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {selectedFiles.map((fileObj) => (
                  <div key={fileObj.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                      {fileObj.type === 'image' ? (
                        <img
                          src={fileObj.preview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                          {/* <Video className="w-8 h-8 text-gray-500" /> */}
                          <video
                            src={fileObj.preview}
                            className="w-full h-full object-cover rounded"
                            muted
                            playsInline
                            preload="metadata"
                            onLoadedData={(e) => {
                              e.target.currentTime = 0.1; // go to first frame
                              URL.revokeObjectURL(e.target.src);
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeFile(fileObj.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      {fileObj.file.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex justify-end gap-4">
            {onClose && (
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t("cancel")}
              </button>
            )}
            <button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || uploading}
              className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t("uploading")}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  {t("upload")} {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto mt-8 shadow-lg ">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
          <div className="flex md:flex-row flex-col items-center justify-between gap-6">
            <div className="flex items-center gap-4 flex-1">
              {/* Icon */}
              <div className="bg-gradient-to-br from-[#3686FF] to-blue-600 rounded-lg w-12 h-12 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 14l4-4 3 3 4-5 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                </svg>
              </div>

              {/* Text content */}
              <div>
                <h4 className="text-gray-900 font-semibold text-lg mb-0.5">
                  Accédez à tous les médias de l'événement
                </h4>
                <p className="text-gray-600 text-sm">
                  Images and vidéos rassemblés pour votre événement
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={`/media-gallery/${token}`}
                className="inline-flex items-center gap-2 bg-[#3686FF] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#2b6fd6] transition-colors duration-200 whitespace-nowrap"
              >
                Accéder aux médias
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className='flex items-center gap-3 mt-4'>
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-150"
              title={copied ? 'Copié' : 'Copier le lien'}
            >
              <Copy className="w-4 h-4" />
              <span className="text-sm">{copied ? 'Copié' : 'Copier'}</span>
            </button>

            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-150"
              title="Partager"
            >
              <Share2 className="w-4 h-4" />
              <span className="text-sm">Partager</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestMediaUpload;
